import React, { useState } from "react";
import {
  Box,
  Checkbox,
  IconButton,
  Chip,
  Collapse,
  Typography,
  Tooltip,
  Button,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import styles from "./document-card.module.scss";

interface DocumentCardProps {
  documentName: string;
  riskTier: string;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  onRemove?: () => void;
  showCheckbox?: boolean;
  checked?: boolean;
  onCheckChange?: (checked: boolean) => void;
  children: React.ReactNode;
  reviewAction?: "view" | "edit" | "none";
  onReviewAction?: () => void;
  reviewStatus?: "verified" | "not-verified" | "pending";
  onReviewStatusChange?: (status: "verified" | "not-verified") => void;
}

const getRiskTierColor = (
  riskTier: string,
): { bg: string; text: string; label: string } => {
  const normalized = riskTier?.toLowerCase();
  if (normalized === "green" || normalized === "low")
    return { bg: "#28a745", text: "#fff", label: "Low risk" };
  if (normalized === "red" || normalized === "high")
    return { bg: "#dc3545", text: "#fff", label: "High risk" };
  if (normalized === "yellow" || normalized === "medium")
    return { bg: "#ffc107", text: "#000", label: "Medium risk" };
  return { bg: "#dc3545", text: "#fff", label: "High risk" };
};

export default function DocumentCard({
  documentName,
  riskTier,
  isExpanded = true,
  onToggleExpand,
  onRemove,
  showCheckbox = false,
  checked = false,
  onCheckChange,
  children,
  reviewAction = "none",
  onReviewAction,
  reviewStatus = "pending",
  onReviewStatusChange,
}: DocumentCardProps): JSX.Element {
  const [expanded, setExpanded] = useState(isExpanded);
  const riskColors = getRiskTierColor(riskTier);

  const handleToggle = () => {
    const newExpanded = !expanded;
    setExpanded(newExpanded);
    onToggleExpand?.();
  };

  return (
    <div className={styles.documentCard}>
      <div className={styles.cardHeader}>
        <div className={styles.headerLeft}>
          {showCheckbox && (
            <Checkbox
              checked={checked}
              onChange={(e) => onCheckChange?.(e.target.checked)}
              size="small"
              sx={{
                color: "#00a888",
                "&.Mui-checked": { color: "#00a888" },
              }}
            />
          )}
          <IconButton
            size="small"
            onClick={handleToggle}
            className={styles.expandButton}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
          <Typography className={styles.documentTitle}>
            {documentName}
          </Typography>
        </div>

        <div className={styles.headerRight}>
          {reviewAction !== "none" && (
            <Box className={styles.reviewSection}>
              {reviewStatus === "pending" && onReviewStatusChange ? (
                <Box className={styles.reviewButtons}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<CheckIcon />}
                    onClick={() => onReviewStatusChange("verified")}
                    className={styles.verifiedButton}
                  >
                    Verified
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => onReviewStatusChange("not-verified")}
                    className={styles.notVerifiedButton}
                  >
                    Not Verified
                  </Button>
                </Box>
              ) : (
                <Chip
                  label={
                    reviewStatus === "verified"
                      ? "✓ Verified"
                      : reviewStatus === "not-verified"
                        ? "○ Not Verified"
                        : "Pending"
                  }
                  size="small"
                  className={
                    reviewStatus === "verified"
                      ? styles.verifiedChip
                      : reviewStatus === "not-verified"
                        ? styles.notVerifiedChip
                        : styles.pendingChip
                  }
                />
              )}
              {reviewAction === "view" && (
                <Tooltip title="View details">
                  <IconButton
                    size="small"
                    onClick={onReviewAction}
                    className={styles.reviewActionButton}
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {reviewAction === "edit" && (
                <Tooltip title="Edit">
                  <IconButton
                    size="small"
                    onClick={onReviewAction}
                    className={styles.reviewActionButton}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
          <Chip
            label={riskColors.label}
            size="small"
            sx={{
              backgroundColor: riskColors.bg,
              color: riskColors.text,
              fontWeight: 600,
              fontSize: "12px",
            }}
          />
          {onRemove && (
            <Tooltip title="Remove">
              <IconButton
                size="small"
                onClick={onRemove}
                className={styles.removeButton}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </div>
      </div>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <div className={styles.cardContent}>{children}</div>
      </Collapse>
    </div>
  );
}
